import { useEffect, useState } from 'react'
import { JiraFieldsCatalog, JiraOptionValue } from 'models'
import { ProductsServiceV4 } from 'services/v4/products-v4.service'

export interface JiraOptionSource {
  values: JiraOptionValue[]
  warning?: string | null
}

const EMPTY_SOURCE: JiraOptionSource = { values: [] }

/* Value endpoint per filterable field, keyed by the catalog's jira_field.
 * Assignee and reporter share the users source. */
const FILTER_VALUE_RESOURCE: Record<string, string> = {
  labels: 'labels',
  components_name: 'components',
  assignee_accountId: 'users',
  reporter_accountId: 'users',
  issuetype_type: 'issue-types',
  resolution_id: 'resolutions'
}

/* Distinct sources behind the six filterable fields. */
export const FILTER_RESOURCES = ['labels', 'components', 'users', 'issue-types', 'resolutions']

/* The sources behind the mapping slots: the connected projects' statuses and
 * every priority defined in the Jira instance. Both wait until the mapping
 * section unlocks. */
export const MAPPING_RESOURCES = ['statuses', 'priorities']

export const STATUSES_RESOURCE = 'statuses'
export const PRIORITIES_RESOURCE = 'priorities'

export const filterOptionsFor = (sources: Record<string, JiraOptionSource>, jiraField: string): JiraOptionSource =>
  sources[FILTER_VALUE_RESOURCE[jiraField]] ?? EMPTY_SOURCE

export const optionSource = (sources: Record<string, JiraOptionSource>, resource: string): JiraOptionSource =>
  sources[resource] ?? EMPTY_SOURCE

/* The server constants that drive the filter constructor and the mapping slots.
 * `scope` carries the connected project: the route takes only the product id and
 * resolves the project from the applied step, so a project change has to refetch
 * even though nothing in the URL moved. */
export const useJiraFieldsCatalog = (productId?: string, enabled?: boolean, scope?: string) => {
  const [catalog, setCatalog] = useState<JiraFieldsCatalog | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !productId) return

    let active = true
    setLoading(true)
    ProductsServiceV4.getJiraFieldsCatalog(productId)
      .then((loaded) => {
        if (active) setCatalog(loaded)
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [productId, enabled, scope])

  return { catalog, loading }
}

/* Option lists for the Data Configuration selects, fetched in parallel. A failing
 * source leaves its select empty instead of taking the whole section down.
 * `scope` refetches on a project change, as for the catalog above. */
export const useJiraOptionSources = (
  productId: string | undefined,
  resources: string[],
  enabled?: boolean,
  scope?: string
) => {
  const [sources, setSources] = useState<Record<string, JiraOptionSource>>({})
  const [loading, setLoading] = useState(false)
  const resourceKey = resources.join(',')

  useEffect(() => {
    if (!enabled || !productId) return

    let active = true
    setLoading(true)

    Promise.all(
      resourceKey.split(',').map((resource) =>
        ProductsServiceV4.getJiraOptions(productId, resource)
          .then((res) => ({ resource, values: res?.values ?? [], warning: res?.warning }))
          /* A failed source must not read as "Jira has no such values" — keep the
           * reason so the section can tell the two apart. */
          .catch((e) => {
            console.error(e)
            return {
              resource,
              values: [] as JiraOptionValue[],
              warning: e?.response?.data?.message || 'Could not be loaded from Jira.'
            }
          })
      )
    )
      .then((loaded) => {
        if (!active) return
        setSources(Object.fromEntries(loaded.map(({ resource, values, warning }) => [resource, { values, warning }])))
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [productId, resourceKey, enabled, scope])

  return { sources, loading }
}
